import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import config from 'config.json';
import ErrorModal from 'components/Modal/errorModal';
import { set } from 'lodash';

type StatusType = 'success' | 'failure' | 'pending';

interface StatusPageProps {
  type: StatusType;
}

const icons: Record<StatusType, JSX.Element> = {
  success: (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" fill="#4CAF50" />
      <path d="M17 8L10.5 15L7 11.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  failure: (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" fill="#F44336" />
      <path d="M15 9L9 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9 9L15 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  pending: (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" fill="#FFC107" />
      <path d="M12 8V12L14 14" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

const StatusPage: React.FC<StatusPageProps> = ({ type }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isWebhookSuccess, setIsWebhookSuccess] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paymentId = params.get('payment_id') ?? '';
    const preferenceId = params.get('preference_id') ?? '';
    const status = params.get('collection_status') ?? '';
    const externalReference = params.get('external_reference') ?? '';
    const reservationId = sessionStorage.getItem('id_reserva_mp') ?? null;
    const isModification = sessionStorage.getItem('es_modificacion') ?? 'false';

    const handleErrorWebhook = async (requestData: any, status: string, error: any) => {
      const url = 'https://ww.fire-bots.com/admins/api/send_notification.php';
      const params = new URLSearchParams({
        envio: '4',
        fullNumber: config.payments_warning_number,
        apikey: '6GALcNuRTQ41',
        nombre: 'Setia_Turismo',
        code: 'error en servicio: ' + JSON.stringify(requestData) + ' status: ' + status + ' error: ' + error,
      });

      try {
        await fetch(`${url}?${params.toString()}`, {
          method: 'GET'
        });
      } catch (error) {
        console.error('Error al enviar notificación:', error);
      } finally {
        setShowErrorModal(true);
      }
    };

    if (type === 'success' || type === 'pending') {
      const requestData = {
        id_pago: paymentId,
        id_preferencia: preferenceId,
        id_reserva_anterior: reservationId || null,
        es_modificacion: isModification === 'true',
      };

      fetch(`${config.backend_url}/webhook`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      })
        .then(response => {
          if (!response.ok) {
            // Si no es un 200, llamamos a handleErrorWebhook con el estado de error
            throw new Error(`HTTP status ${response.status}`);
          }
          sessionStorage.removeItem('id_reserva_mp');
          return response.json();
        })
        .then(data => setIsWebhookSuccess(true))
        .catch(error => handleErrorWebhook(requestData, status, error.message));
    }
  }, [type, location.search]);

  const messages: Record<StatusType, string> = {
    success: 'Pago realizado con éxito, gracias por tu reserva',
    failure: 'Error en el pago, intentalo nuevamente por favor',
    pending: 'Pago pendiente de confirmación, cuando el pago haya sido confirmado se enviara un correo de confirmación',
  };

  const handleButtonClick = () => {
    if (type === 'success' || type === 'pending') {
      navigate('/');
    } else {
      navigate('/');
    }
  };
  
  const handleCloseModal = () => {
    setShowErrorModal(false);
    window.location.href = '/';
  }

  return (
    <>
      <ErrorModal
        message="Lo sentimos, no se pudo completar tu pago en este momento. Por favor, verifica los detalles de tu tarjeta o método de pago, e intenta nuevamente. Si el problema persiste, contacta a nuestro soporte con el siguiente código de referencia: ERR-PAGO-001."
        isModalOpen={showErrorModal}
        handleModalClose={() => handleCloseModal()}
      />

      {!isWebhookSuccess && (
        <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
          
        </div>
      )}
      {isWebhookSuccess && (
        <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
          {icons[type]}
          <h1 className="text-2xl flex items-center text-center font-semibold mt-4">{messages[type]}</h1>
          <button
            onClick={handleButtonClick}
            className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700"
          >
            {type === 'failure' ? 'Ir a la Home' : 'Ir a la Home'}
          </button>
        </div>
      )}
    </>
  );
};

export default StatusPage;